// src/components/asset-store.jsx
// Paystack removed. Monnify SDK used for card payments when wallet balance is insufficient.

import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, doc, updateDoc,
  arrayUnion, increment, addDoc, serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

export default function AssetStore({ balance, stylistId, stylistName, stylistEmail, onClose }) {
  const [assets,         setAssets]         = useState([]);
  const [buying,         setBuying]         = useState(null);
  const [directPayAsset, setDirectPayAsset] = useState(null);
  const [processing,     setProcessing]     = useState(false);
  const [error,          setError]          = useState(null);

  useEffect(() => {
    return onSnapshot(collection(db, 'assets'), snap => {
      setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // ── Finalize purchase in Firestore ─────────────────────────────────────────
  const finalizePurchase = async (asset, isDirect) => {
    try {
      const barberRef = doc(db, 'barbers', stylistId);
      const updates = { purchasedAssets: arrayUnion(asset.fileUrl) };
      if (!isDirect) updates.walletBalance = increment(-asset.price);
      await updateDoc(barberRef, updates);

      await addDoc(collection(db, 'store_sales'), {
        assetId:   asset.id,
        assetName: asset.name,
        amount:    asset.price,
        stylistId,
        method:    isDirect ? 'monnify_card' : 'wallet',
        timestamp: serverTimestamp(),
      });

      await generateReceipt(stylistEmail, stylistName, asset.name, asset.price);
      alert(`Success! ${asset.name} is now available.`);
    } catch { alert('Purchase failed. Please try again.'); }
  };

  // ── Buy with wallet ────────────────────────────────────────────────────────
  const purchaseWithWallet = async (asset) => {
    if (balance < asset.price) {
      setDirectPayAsset(asset);
      return;
    }
    if (!window.confirm(`Purchase ${asset.name} for ₦${asset.price.toLocaleString()}?`)) return;
    setBuying(asset.id);
    await finalizePurchase(asset, false);
    setBuying(null);
  };

  // ── Pay with Monnify card (when wallet insufficient) ──────────────────────
  const handleMonnifyPayment = () => {
    if (!directPayAsset) return;
    setError(null);

    if (!window.MonnifySDK) {
      setError('Payment gateway not loaded. Please refresh the page.');
      return;
    }

    const apiKey       = process.env.REACT_APP_MONNIFY_API_KEY;
    const contractCode = process.env.REACT_APP_MONNIFY_CONTRACT_CODE;

    if (!apiKey || !contractCode) {
      setError('Payment gateway not configured. Please contact support.');
      return;
    }

    setProcessing(true);

    window.MonnifySDK.initialize({
      amount:             directPayAsset.price,
      currency:           'NGN',
      reference:          `AST_${stylistId.slice(0,6)}_${Date.now()}`,
      customerFullName:   stylistName || 'Esdras Stylist',
      customerEmail:      stylistEmail || auth.currentUser?.email || 'stylist@esdras.app',
      apiKey,
      contractCode,
      paymentDescription: `Esdras 3D Asset: ${directPayAsset.name}`,
      metadata: {
        assetId:   directPayAsset.id,
        stylistId,
        type:      'asset_purchase',
      },
      onComplete: async (response) => {
        setProcessing(false);
        if (response.status === 'SUCCESS') {
          await finalizePurchase(directPayAsset, true);
          setDirectPayAsset(null);
        } else {
          setError('Payment was not completed. Please try again.');
        }
      },
      onClose: () => setProcessing(false),
    });
  };

  const generateReceipt = async (email, name, itemName, price) => {
    await addDoc(collection(db, 'mail'), {
      to: email || auth.currentUser?.email,
      message: {
        subject: 'Receipt: Your Esdras 3D Asset Purchase',
        html: `<div style="background:#001F3F;color:white;padding:20px;border:1px solid #B8860B;">
          <h2>Esdras</h2><p>Stylist: ${name}</p><p>Item: ${itemName}</p>
          <p>Price: ₦${price.toLocaleString()}</p><p>Status: PAID via Monnify</p></div>`,
      },
    });
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ color: GOLD, margin: 0 }}>3D ASSET STORE</h2>
          <button onClick={onClose} style={closeX}>✕</button>
        </div>
        <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '16px' }}>
          Available Credits: ₦{balance.toLocaleString()}
        </p>

        {error && (
          <div style={{ color: '#ff4d4d', background: 'rgba(255,77,77,0.1)', padding: '12px', borderRadius: '10px', marginBottom: '16px', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {/* MONNIFY CHECKOUT */}
        {directPayAsset ? (
          <div style={{ padding: '20px', textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: '15px' }}>
            <h3 style={{ color: GOLD }}>Checkout: {directPayAsset.name}</h3>
            <p style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>₦{directPayAsset.price.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '16px' }}>
              Your wallet balance is insufficient. Pay by card via Monnify.
            </p>
            <button
              onClick={handleMonnifyPayment}
              disabled={processing}
              style={{ ...buyBtn, marginBottom: '10px', opacity: processing ? 0.7 : 1 }}
            >
              {processing ? 'VERIFYING...' : `PAY ₦${directPayAsset.price.toLocaleString()} VIA MONNIFY`}
            </button>
            <button
              onClick={() => { setDirectPayAsset(null); setError(null); }}
              style={{ background: 'none', border: '1px solid white', color: 'white', padding: '10px', borderRadius: '5px', cursor: 'pointer', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={assetGrid}>
            {assets.map(asset => (
              <div key={asset.id} style={assetCard}>
                <img src={asset.previewUrl} style={previewImg} alt="Asset" />
                <p style={{ fontWeight: 'bold', margin: '8px 0 2px', fontSize: '0.9rem' }}>{asset.name}</p>
                <p style={{ color: GOLD, margin: '0 0 10px', fontSize: '0.8rem' }}>₦{asset.price.toLocaleString()}</p>
                <button
                  onClick={() => purchaseWithWallet(asset)}
                  disabled={buying === asset.id}
                  style={{ ...buyBtn, opacity: buying === asset.id ? 0.5 : 1 }}
                >
                  {balance < asset.price
                    ? 'PAY WITH CARD'
                    : buying === asset.id ? 'Processing...' : 'BUY WITH WALLET'
                  }
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlay    = { position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:5000, display:'grid', placeItems:'center' };
const modal      = { background:NAVY, width:'90%', maxWidth:'600px', height:'80vh', borderRadius:'30px', padding:'2rem', overflowY:'auto', border:`1px solid ${GOLD}` };
const assetGrid  = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'15px', marginTop:'20px' };
const assetCard  = { background:'rgba(255,255,255,0.05)', padding:'10px', borderRadius:'15px', textAlign:'center', border:'1px solid rgba(255,255,255,0.1)' };
const previewImg = { width:'100%', height:'100px', objectFit:'cover', borderRadius:'10px', background:'#000' };
const buyBtn     = { background:GOLD, border:'none', padding:'12px', borderRadius:'8px', width:'100%', fontWeight:'bold', cursor:'pointer', fontSize:'0.75rem', color:'black' };
const closeX     = { background:'none', border:'none', color:'white', fontSize:'1.5rem', cursor:'pointer' };
